import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { EmailChangeIntent, EmailChangeService } from 'app/account/user-settings/user-change-email/email-change.service';
import { forkJoin, from, map, of, switchMap, take } from 'rxjs';
import { IAuth0CallbackTokens, UniversalLoginService } from '../services/universal-login.service';
import { EmailVerificationDialogService } from '../email-verification-dialog/email-verification-dialog.service';

/**
 * Login Callback Component
 * 
 * Completes the Auth0 SPA SDK redirect callback and initializes the application session.
 * 
 * Route: /login/callback
 * Query Parameters: code, state
 * 
 * @example
 * // Auth0 redirects to: http://localhost:4200/login/callback?code=abc123&state=state_123
 */
@Component({
    selector: 'app-login-callback',
    templateUrl: './login-callback.component.html',
    styleUrls: ['./login-callback.component.scss'],
})
export class LoginCallbackComponent implements OnInit {
    public isProcessing = true;
    public errorMessage: string | null = null;
    public isLoggingOut = false;

    public constructor(
        private router: Router,
        private universalLoginService: UniversalLoginService,
        private storage: StorageMap,
        private authenticationService: AuthenticationService,
        private emailVerificationDialogService: EmailVerificationDialogService,
        private emailChangeService: EmailChangeService,
    ) {
    }

    public ngOnInit(): void {
        this.handleAuthorizationCode();
    }

    public async backToLogin(): Promise<void> {
        if (this.isLoggingOut) {
            return;
        }

        this.isLoggingOut = true;
        try {
            await this.authenticationService.logout();
        } catch {
            console.error('[LoginCallback] Failed to log out after authentication failure.');
            this.errorMessage = 'Unable to sign out. Please try again or contact support.';
        } finally {
            this.isLoggingOut = false;
        }
    }

    /**
     * Handle the authorization code from Auth0
     */
    private handleAuthorizationCode(): void {
        from(this.universalLoginService.handleRedirectCallback()).pipe(
            switchMap((callbackState) =>
                from(this.universalLoginService.getCallbackTokens()).pipe(
                    map((tokens) => ({ callbackState, tokens })),
                ),
            ),
        ).subscribe({
            next: ({ callbackState, tokens }) => {
                this.handleTokenResponse(tokens, callbackState.returnTo);
            },
            error: () => this.handleError('callback_failed', 'Unable to complete authentication'),
        });
    }

    /**
     * Handle successful token response
     */
    private handleTokenResponse(tokenResponse: IAuth0CallbackTokens, returnTo = '/dashboard'): void {
        this.universalLoginService.consumePendingIntent<EmailChangeIntent>().pipe(
            take(1),
            switchMap((pending) => {
                if (pending?.intent !== 'email_change' && pending?.intent !== 'email_change_restore') {
                    return of({ pending: null, cachedToken: '' });
                }
                if (!pending.data || typeof pending.data !== 'object') {
                    throw new Error('Invalid email-change intent');
                }
                return forkJoin({
                    loggedIn: this.authenticationService.isLoggedIn().pipe(take(1)),
                    hint: this.storage.get<string>('auth0_username', { type: 'string' }).pipe(take(1)),
                }).pipe(
                    switchMap(({ loggedIn, hint }) => from(
                        loggedIn
                            ? this.universalLoginService.getAuthenticatedAccessToken(
                                pending.intent === 'email_change_restore',
                            )
                            : Promise.resolve(null),
                    ).pipe(map((cachedToken) => ({
                        pending: { currentEmail: hint || '' } satisfies EmailChangeIntent,
                        cachedToken: cachedToken || '',
                    })))),
                );
            }),
        ).subscribe({
            next: ({ pending, cachedToken }) => {
                if (pending && !this.authenticationService.isAuthTimeRecent(tokenResponse.id_token)) {
                    this.handleError('reauth_stale', 'Reauthentication was not recent enough to change email');
                    return;
                }
                if (pending && cachedToken) {
                    this.storage.delete('pkce_code_verifier').subscribe();
                    this.storage.delete('pkce_state').subscribe();
                    this.isProcessing = false;
                    this.resumeEmailChange(pending.currentEmail, pending, tokenResponse, cachedToken);
                } else {
                    const username = tokenResponse.userName || null;
                    this.storeAndCompleteAuth(
                        tokenResponse,
                        pending?.currentEmail || username,
                        pending,
                        returnTo,
                    );
                }
            },
            error: () => this.handleError('session_lookup_failed', 'Unable to read the authentication session'),
        });
    }

    private storeAndCompleteAuth(
        tokenResponse: IAuth0CallbackTokens,
        username: string | null,
        pending?: EmailChangeIntent,
        returnTo = '/dashboard',
    ): void {
        if (pending) {
            // Step-up ID tokens are proof for this operation, not persistent session data.
            this.completeAuth(tokenResponse, username, pending);
            return;
        }
        if (username) {
            this.storage.set('auth0_username', username, { type: 'string' }).subscribe(() => {
                this.completeAuth(tokenResponse, username, pending, returnTo);
            });
            return;
        }
        this.completeAuth(tokenResponse, username, pending, returnTo);
    }

    /**
     * Complete authentication by initializing app session from Auth0 tokens.
     */
    private completeAuth(
        tokenResponse: IAuth0CallbackTokens,
        extractedUsername: string | null,
        pending?: EmailChangeIntent,
        returnTo = '/dashboard',
    ): void {
        this.storage.get<string>('auth0_username', { type: 'string' }).subscribe((storedUsername) => {
            const username = extractedUsername || storedUsername?.trim();
            if (!tokenResponse?.access_token || !tokenResponse?.id_token) {
                this.handleError('missing_tokens', 'Auth0 token response is incomplete');
                return;
            }

            this.authenticationService.completeAuth0Login(
                username || '',
                tokenResponse.access_token,
                tokenResponse.id_token,
                tokenResponse.expires_in,
            ).subscribe({
                next: (response) => {
                    // Clear PKCE parameters.
                    this.storage.delete('pkce_code_verifier').subscribe();
                    this.storage.delete('pkce_state').subscribe();

                    this.isProcessing = false;

                    if (pending) {
                        this.resumeEmailChange(
                            username,
                            { ...pending, apiSessionRestored: true },
                            tokenResponse,
                        );
                        return;
                    }

                    if (response?.verify_email === true || response?.auth?.verify_email === true) {
                        this.emailVerificationDialogService.open();
                    }
                    this.router.navigate([returnTo || '/dashboard']);
                },
                error: (error) => {
                    console.error('[LoginCallback] Failed to initialize app session after Auth0.');
                    this.handleError('session_initialization_failed', 'Unable to complete application login');
                },
                complete: () => {
                    if (this.isProcessing) {
                        this.handleError(
                            'session_initialization_incomplete',
                            'Application session initialization completed without a response',
                        );
                    }
                },
            });
        });
    }

    /**
     * Resume an email-change request after the user completes a step-up (prompt=login) reauth.
     * Requires a fresh auth_time to prove the login just happened, not a replayed/cached token.
     */
    private resumeEmailChange(
        username: string,
        pending: EmailChangeIntent,
        tokenResponse: any,
        accessToken: string = tokenResponse.access_token,
    ): void {
        if (!pending?.currentEmail || !this.authenticationService.isAuthTimeRecent(tokenResponse.id_token)) {
            this.handleError('reauth_stale', 'Reauthentication was not recent enough to change email');
            return;
        }

        this.emailChangeService.run(pending, accessToken, tokenResponse.id_token);
        this.router.navigate(['/user-settings']);
    }

    /**
     * Handle authentication error
     */
    private handleError(error: string, _description?: string): void {
        this.isProcessing = false;
        this.errorMessage = error === 'access_denied'
            ? 'Sign-in was not completed. Please try again.'
            : 'Unable to complete authentication. Please try again or contact support.';
        console.error('[LoginCallback] Authentication could not be completed.');

        // Clear sensitive data
        this.universalLoginService.clearAuth0Session();
    }

}
